import React, { useState } from "react";
import { useDeal } from "../lib/store";
import { parseApi, streamPost, streamPostForm } from "../lib/api";
import { parseLoose, num } from "../lib/useFinancialParse";
import { Card } from "./shared";
import { Presentation, CheckCircle2, Sparkles } from "lucide-react";
import { fmt$ } from "../lib/model";
import { toast } from "sonner";

const FIELDS = [
  { k: "ttmRevenue", label: "TTM revenue", kind: "money" },
  { k: "sde", label: "Adjusted SDE / EBITDA", kind: "money" },
  { k: "grossMarginPct", label: "Gross margin", kind: "pct" },
  { k: "employeeCount", label: "Employees", kind: "num" },
  { k: "yearEstablished", label: "Year established", kind: "num" },
  { k: "recapMult", label: "Recap / entry multiple", kind: "x", recap: true },
  { k: "existingDebt", label: "Existing debt", kind: "money", recap: true },
  { k: "newDebt", label: "New senior facility", kind: "money", recap: true },
  { k: "cashOutPct", label: "Legacy cash-out", kind: "pct", recap: true },
  { k: "warChest", label: "Acquisition war chest", kind: "money", recap: true },
  { k: "equityAsk", label: "LP equity ask", kind: "money" },
  { k: "rate", label: "Senior rate", kind: "pct" },
  { k: "loanYrs", label: "Senior term (years)", kind: "num" },
  { k: "exitMult", label: "Exit multiple", kind: "x" },
  { k: "hold", label: "Hold period (years)", kind: "num" },
  { k: "prefReturn", label: "LP preferred return", kind: "pct" },
  { k: "lpSplit", label: "LP profit split", kind: "pct" },
];

const show = (v, kind) => (v === "" || v == null ? "" : kind === "money" ? fmt$(v) : kind === "pct" ? `${v}%` : kind === "x" ? `${v}×` : String(v));
const lines = (a) => (Array.isArray(a) ? a : []).filter(Boolean).join("\n");
const pairs = (a) => (Array.isArray(a) ? a : []).map((x) => `${x.title || ""}${x.text ? `: ${x.text}` : ""}`).join("\n");
const toPairs = (t) => (t || "").split("\n").filter((l) => l.trim()).map((l) => {
  const i = l.indexOf(":");
  return i > 0 ? { title: l.slice(0, i).trim(), text: l.slice(i + 1).trim() } : { title: l.trim(), text: "" };
});
const addOnLines = (a) => (Array.isArray(a) ? a : []).map((x) => [x.name, x.location, x.revenue ? fmt$(x.revenue) : "", x.note].filter(Boolean).join(" | ")).join("\n");
const toAddOns = (t) => (t || "").split("\n").filter((l) => l.trim()).map((l) => {
  const [name, location, revenue, note] = l.split("|").map((s) => (s || "").trim());
  return { name, location, revenue: num(revenue) || 0, note: note || "" };
});

export function DeckImport() {
  const { M, patch, setDealName, requestSave } = useDeal();
  const [doc, setDoc] = useState(null);
  const [rows, setRows] = useState(null);
  const [find, setFind] = useState(null);
  const [hist, setHist] = useState(null);

  const load = async (file) => {
    const fn = file.name.toLowerCase();
    if (!/\.(pdf|docx|pptx)$/.test(fn)) return toast.error("Decks must be PDF, DOCX or PPTX");
    setDoc({ name: file.name, size: file.size, status: "parsing" });
    try {
      let full = "";
      const fd = new FormData();
      fd.append("file", file);
      if (fn.endsWith(".pdf")) {
        fd.append("mode", "deck");
        await streamPostForm("/parse/analyze-pdf", fd, (c, f) => { full = f; });
      } else {
        const ex = await parseApi.extractText(fd);
        await streamPost("/parse/analyze", { text: ex.text, kind: "deck" }, (c, f) => { full = f; });
      }
      const p = parseLoose(full) || {};
      const val = (k) => num(k === "sde" ? (p.sde ?? p.ebitda) : p[k]);
      const found = FIELDS.map((f) => ({ ...f, value: val(f.k) ?? "", on: val(f.k) != null && val(f.k) !== 0 }))
        .filter((f) => f.value !== "" || f.on);
      if (!found.length && !p.findings) throw new Error("nothing extracted");
      setRows(found);
      setHist(Array.isArray(p.historicalYears) ? p.historicalYears.filter((y) => y && y.year) : []);
      const f = p.findings || {};
      setFind({ thesis: lines(f.thesis), strengths: pairs(f.strengths), risks: pairs(f.risks), addOns: addOnLines(f.addOns), marketNotes: lines(f.marketNotes),
                dealName: p.dealName || "", companyName: p.companyName || "", industry: p.industry || "", address: p.address || "" });
      setDoc({ name: file.name, size: file.size, status: "parsed" });
      toast.success(`Read ${file.name} — review and edit before applying`);
    } catch (e) {
      setDoc({ name: file.name, size: file.size, status: "error" });
      toast.error(e?.response?.data?.detail || "Could not read the deck — try a text-based PDF or the PPTX");
    }
  };

  const setRow = (k, p) => setRows((r) => r.map((x) => (x.k === k ? { ...x, ...p } : x)));

  const apply = () => {
    const on = (rows || []).filter((r) => r.on && r.value !== "" && num(r.value) != null);
    const top = {}, rec = {};
    on.forEach((r) => { (r.recap ? rec : top)[r.k] = num(r.value); });
    const upd = { ...top };
    if (hist?.length) upd.historicalYears = hist.map((y) => ({ year: String(y.year), revenue: num(y.revenue) || 0, sde: num(y.sde) || 0, gm: num(y.gm) || M.grossMarginPct || 0, netIncome: num(y.netIncome) || 0 }));
    if (Object.keys(rec).length) upd.recap = { ...(M.recap || {}), ...rec };
    const sde = top.sde ?? M.sde, mult = rec.recapMult ?? (M.recap || {}).recapMult;
    if (sde && mult) upd.askPrice = sde * mult;
    if (find) {
      upd.deckFindings = {
        thesis: find.thesis.split("\n").filter((x) => x.trim()), strengths: toPairs(find.strengths), risks: toPairs(find.risks),
        addOns: toAddOns(find.addOns), marketNotes: find.marketNotes.split("\n").filter((x) => x.trim()), source: doc?.name || "",
      };
      if (find.industry && !M.industry) upd.industry = find.industry;
      if (find.address && !M.address) upd.address = find.address;
      if (find.companyName && !M.companyName) upd.companyName = find.companyName;
    }
    patch(upd);
    if (find?.dealName && !M.dealName) setDealName(find.dealName);
    setTimeout(requestSave, 50);
    toast.success(`Applied ${on.length} input${on.length === 1 ? "" : "s"}${hist?.length ? ` + ${hist.length} historical year${hist.length === 1 ? "" : "s"}` : ""} — findings will auto-fill the deal room narrative`);
  };

  return (
    <div data-testid="deck-import">
      <Card title="Pitch deck / key findings (recap & platform)" testid="deck-import-card">
        <div className="ra-sub" style={{ marginBottom: 10 }}>
          Drop the recap deck or a key-findings memo. Nothing touches the model until you review the values and hit apply.
        </div>
        <div className="drop" onClick={() => document.getElementById("deck-input").click()}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) load(e.dataTransfer.files[0]); }}
          data-testid="drop-deck">
          <div className="icon"><Presentation size={28} /></div>
          <div className="t">Drop a deck or click to browse</div>
          <div className="s">PDF · PPTX · DOCX</div>
          <input id="deck-input" type="file" accept=".pdf,.pptx,.docx" style={{ display: "none" }}
            onChange={(e) => e.target.files[0] && load(e.target.files[0])} data-testid="input-deck" />
        </div>
        {doc && (
          <div style={{ marginTop: 10, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }} data-testid="status-deck">
            {doc.status === "parsed" && <CheckCircle2 size={15} color="var(--lime)" />}
            <span>{doc.name} · {(doc.size / 1024).toFixed(0)} KB</span>
            <span className={doc.status === "parsed" ? "pos" : doc.status === "error" ? "neg" : "neu"}>
              {doc.status === "parsing" ? "Reading the deck… (~30s)" : doc.status === "parsed" ? "✓ Ready to review" : "Error"}
            </span>
          </div>
        )}
      </Card>

      {rows && (
        <div className="grid g2" style={{ marginTop: 12 }}>
          <Card title="Review extracted inputs" className="table-scroll" testid="deck-review">
            <table className="tbl">
              <thead><tr><th>Apply</th><th>Input</th><th className="num">From the deck</th><th className="num">Use this value</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.k} data-testid={`deck-row-${r.k}`}>
                    <td><input type="checkbox" checked={r.on} onChange={(e) => setRow(r.k, { on: e.target.checked })} data-testid={`deck-on-${r.k}`} /></td>
                    <td>{r.label}{r.recap ? <span className="pill" style={{ marginLeft: 6 }}>recap</span> : null}</td>
                    <td className="num mono" style={{ color: "var(--text3)" }}>{show(r.value, r.kind)}</td>
                    <td className="num"><input className="inp num" style={{ maxWidth: 130 }} value={r.value} onChange={(e) => setRow(r.k, { value: e.target.value, on: true })} data-testid={`deck-val-${r.k}`} /></td>
                  </tr>
                ))}
                {hist?.length > 0 && (
                  <tr data-testid="deck-row-hist">
                    <td><input type="checkbox" checked readOnly /></td>
                    <td>Historical P&amp;L</td>
                    <td className="num" style={{ color: "var(--text3)" }}>{hist.length} years</td>
                    <td className="num mono">{hist.map((y) => y.year).join(", ")}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <button className="btn btn-share" style={{ marginTop: 10 }} onClick={apply} data-testid="deck-apply">
              <Sparkles size={12} style={{ marginRight: 6 }} />Apply selected to the model
            </button>
          </Card>

          <Card title="Findings — edit before they reach the deal room" testid="deck-findings">
            {[["thesis", "Investment thesis (one bullet per line)"], ["strengths", "Strengths (Title: one sentence)"], ["risks", "Risks (Title: one sentence)"],
              ["addOns", "Add-on pipeline (Name | City, ST | revenue | note)"], ["marketNotes", "Market notes (one per line)"]].map(([k, label]) => (
              <div className="field" key={k}>
                <label>{label}</label>
                <textarea className="inp" rows={k === "thesis" ? 4 : 3} value={find[k]} onChange={(e) => setFind({ ...find, [k]: e.target.value })} data-testid={`deck-find-${k}`} />
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
